/**
 * Generic optimistic-update helper.
 *
 * Immediately writes `next` via `write`, then awaits `commit()`.
 * If `commit` rejects the snapshot captured before the write is restored
 * and the error is re-thrown.
 *
 * Works with plain getter/setter pairs, @preact/signals, or any
 * signal-compatible read/write interface.
 *
 * @example
 * await optimistic({
 *   read:   () => taskSignal.value,
 *   write:  (v) => { taskSignal.value = v; },
 *   next:   { ...task, done: true },
 *   commit: () => api.patch(`/tasks/${task.id}`, { done: true }),
 * });
 */
export async function optimistic<T>(opts: {
  read: () => T;
  write: (v: T) => void;
  next: T;
  commit: () => Promise<unknown>;
}): Promise<void> {
  const { read, write, next, commit } = opts;
  const snapshot = read();
  write(next);
  try {
    await commit();
  } catch (err) {
    write(snapshot);
    throw err;
  }
}
